import { useMemo } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { mapRejectionReasonCode } from '../mapRejectionReasonCode'

import { RejectionReasonsCodeType, VrQcVerdict } from '@percent/admin-dashboard/api/types'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import {
  ActionModal,
  Alert,
  Flex,
  FormField,
  Modal,
  Select,
  SelectOption,
  TextArea,
  TextInput
} from '@percent/lemonade'

const REASONING_MAX_LENGTH = 2000

type QcDecision = 'accept' | 'reject' | 'merge'

type SubmitQcFormValues = {
  decision: QcDecision | ''
  rejectionReasonCode: string
  organisationId: string
  reasoning: string
}

type SubmitQcModalProps = {
  isOpen: boolean
  onClose: () => void
  validationRequestId: string
  finishAction: () => void
}

export function SubmitQcModal({ isOpen, onClose, validationRequestId, finishAction }: SubmitQcModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const toast = useToast()

  const validationSchema = yup.object().shape({
    decision: yup.string().oneOf(['accept', 'reject', 'merge']).required(),
    rejectionReasonCode: yup.string().when('decision', {
      is: 'reject',
      then: schema => schema.required(),
      otherwise: schema => schema.notRequired()
    }),
    organisationId: yup.string().when('decision', {
      is: 'merge',
      then: schema => schema.trim().required(),
      otherwise: schema => schema.notRequired()
    }),
    reasoning: yup.string().max(REASONING_MAX_LENGTH).notRequired()
  })

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid }
  } = useForm<SubmitQcFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      decision: '',
      rejectionReasonCode: '',
      organisationId: '',
      reasoning: ''
    }
  })

  const decision = useWatch({ control, name: 'decision' })

  const handleClose = () => {
    reset()
    onClose()
  }

  const [{ isLoading: isSubmitting }, { apiFunc: submitQc }] = useMutation(
    adminService.submitValidationRequestQc,
    () => {
      toast(t('toast.qcSubmitSuccess'), 'success')
      finishAction()
      handleClose()
    },
    () => {
      toast(t('toast.qcSubmitError'), 'error')
    }
  )

  const verdictOptions: SelectOption[] = useMemo(
    () => [
      { label: t('button.accept'), value: 'accept' },
      { label: t('button.reject'), value: 'reject' },
      { label: t('dropdown.qcVerdict.merge'), value: 'merge' }
    ],
    [t]
  )

  const rejectionReasonOptions: SelectOption[] = useMemo(
    () =>
      Object.values(RejectionReasonsCodeType).map(code => ({
        label: t(mapRejectionReasonCode[code] as LocaleKey),
        value: code
      })),
    [t]
  )

  const onSubmit = ({
    decision: verdictDecision,
    rejectionReasonCode,
    organisationId,
    reasoning
  }: SubmitQcFormValues) => {
    let verdict: VrQcVerdict

    if (verdictDecision === 'reject') {
      verdict = { decision: 'reject', rejectionReasonCode }
    } else if (verdictDecision === 'merge') {
      verdict = { decision: 'merge', organisationId: organisationId.trim() }
    } else {
      verdict = { decision: 'accept' }
    }

    submitQc({
      id: validationRequestId,
      payload: { verdict, reasoning: reasoning.trim() || null }
    })
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="submit-qc-modal">
      <ActionModal
        title={t('dialog.submitQc.title')}
        primaryButtonText={t('dialog.submitQc.title')}
        primaryBtnTestId="qc-submit-button"
        secondaryButtonText={t('button.cancel')}
        secondaryBtnTestId="qc-cancel-button"
        handleClose={handleClose}
        handleSubmit={handleSubmit(onSubmit)}
        disabled={!isValid}
        loading={isSubmitting}
      >
        <Flex direction="column" gap={16}>
          <Alert variant="info">{t('dialog.submitQc.description')}</Alert>

          <Controller
            name="decision"
            control={control}
            render={({ field: { value } }) => (
              <FormField label={t('dialog.submitQc.verdict')} necessity="required">
                <Select
                  placeholder={t('dialog.submitQc.selectVerdict')}
                  options={verdictOptions}
                  searchable={false}
                  defaultValue={verdictOptions.find(option => option.value === value)}
                  onChange={option => {
                    setValue('decision', option.value as QcDecision, { shouldValidate: true })
                    setValue('rejectionReasonCode', '', { shouldValidate: true })
                    setValue('organisationId', '', { shouldValidate: true })
                  }}
                  data-testid="qc-verdict-select"
                />
              </FormField>
            )}
          />

          {decision === 'reject' && (
            <Controller
              name="rejectionReasonCode"
              control={control}
              render={({ field: { value } }) => (
                <FormField label={t('form.reasonForRejection')} necessity="required">
                  <Select
                    placeholder={t('form.selectRejectionReason')}
                    options={rejectionReasonOptions}
                    searchable={false}
                    defaultValue={rejectionReasonOptions.find(option => option.value === value)}
                    onChange={option => setValue('rejectionReasonCode', option.value, { shouldValidate: true })}
                    data-testid="qc-rejection-reason-select"
                  />
                </FormField>
              )}
            />
          )}

          {decision === 'merge' && (
            <Controller
              name="organisationId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormField label={t('table.organisationId')} necessity="required">
                  <TextInput
                    name="organisationId"
                    value={value}
                    placeholder={t('dialog.submitQc.organisationIdPlaceholder')}
                    onChange={onChange}
                    data-testid="qc-merge-organisation-input"
                  />
                </FormField>
              )}
            />
          )}

          <Controller
            name="reasoning"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormField label={t('dialog.submitQc.reasoning')} necessity="optional" hideCharacterCount>
                <TextArea
                  name="reasoning"
                  value={value}
                  maxLength={REASONING_MAX_LENGTH}
                  placeholder={t('dialog.submitQc.reasoningPlaceholder')}
                  onChange={onChange}
                  data-testid="qc-reasoning"
                />
              </FormField>
            )}
          />
        </Flex>
      </ActionModal>
    </Modal>
  )
}
